import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Circle,
  InfoWindow,
} from "@react-google-maps/api";
import {
  ButtonBase,
  IconButton,
  makeStyles,
  CircularProgress,
  colors,
  Typography,
  Box,
} from "@material-ui/core";
import GpsFixedIcon from "@material-ui/icons/GpsFixed";
import CloseIcon from "@material-ui/icons/Close";
import { useHistory } from "react-router-dom";

// 這些常數與 makeStyles 移到元件外，避免每次 render 重新建立物件。
const containerStyle = {
  width: "100%",
  height: "100%",
  position: "relative",
};

// 資料尚未含座標時的後備中心（台北），只是初始畫面用，不影響最終定位。
const defaultCenter = { lat: 25.033964, lng: 121.564468 };

const circleOptions = {
  strokeColor: colors.blue["A100"],
  strokeOpacity: 0.8,
  strokeWeight: 2,
  fillColor: colors.blue["A100"],
  fillOpacity: 0.35,
  clickable: false,
  draggable: false,
  editable: false,
  visible: true,
  radius: 500,
  zIndex: 1,
};

const useStyles = makeStyles({
  GpsButton: {
    position: "absolute",
    right: 10,
    top: 60,
    height: 40,
    width: 40,
    borderRadius: 2,
    background: "rgb(255, 255, 255) none repeat scroll 0% 0%",
    boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 4px -1px",
    color: "#666666",
  },
});

function createKey(location) {
  return location.lat + location.lng;
}

export default function GoogleMaps({ style, data }) {
  const classes = useStyles();
  // useJsApiLoader 會全域快取 Google Maps script，避免元件重繪／換頁時重複載入。
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyD676hJpWhvLy86aWs2_sQWk1g48bBD2eo",
  });
  const mapRef = useRef(null);
  const [ownPosition, setOwnPosition] = useState(null);

  const points = data || [];
  // 初始中心用第一個催眠師座標；地圖不必等 GPS 就能立即顯示。
  // 依賴用原始數值，資料穩定時 center 參考不變，使用者可自由拖動地圖。
  const firstLat = points[0] && points[0].lat;
  const firstLng = points[0] && points[0].lng;
  const center = useMemo(
    () =>
      firstLat != null && firstLng != null
        ? { lat: firstLat, lng: firstLng }
        : defaultCenter,
    [firstLat, firstLng]
  );

  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);
  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) return;
    // 定位是背景進行，成功後畫出使用者藍圈並平滑移到使用者位置，但不阻擋地圖顯示。
    navigator.geolocation.getCurrentPosition((e) => {
      const pos = { lat: e.coords.latitude, lng: e.coords.longitude };
      setOwnPosition(pos);
      if (mapRef.current) mapRef.current.panTo(pos);
    });
  }, []);

  const _watchPosition = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.watchPosition((e) => {
      const pos = { lat: e.coords.latitude, lng: e.coords.longitude };
      setOwnPosition(pos);
      if (mapRef.current) mapRef.current.panTo(pos);
    });
  }, []);

  // 只等 script 載入（isLoaded），不再等 GPS 定位。
  if (!isLoaded) {
    return <CircularProgress />;
  }

  return (
    <GoogleMap
      mapContainerStyle={style || containerStyle}
      center={center}
      zoom={15}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      {/* Child components, such as markers, info windows, etc. */}
      {ownPosition && <Circle center={ownPosition} options={circleOptions} />}
      {points.map((item) => (
        <MakerItem data={item} key={createKey(item)} />
      ))}
      <ButtonBase
        variant="contained"
        classes={{ root: classes.GpsButton }}
        onClick={_watchPosition}
      >
        <GpsFixedIcon />
      </ButtonBase>
    </GoogleMap>
  );
}

function MakerItem({ data }) {
  const history = useHistory();
  const [openWindow, setOpenWindow] = useState(true);
  return (
    <Marker
      position={data}
      onClick={() => {
        if (openWindow) {
          history.push(`/hypnotist/${data.id}`);
        } else {
          setOpenWindow(true);
        }
      }}
    >
      {openWindow && (
        <InfoWindow onCloseClick={() => setOpenWindow(false)}>
          {/* 自訂關閉按鈕，與名稱同一行（Google 預設的關閉列已用 CSS 隱藏） */}
          <Box display="flex" alignItems="center">
            <Typography>{data.title}</Typography>
            <IconButton
              size="small"
              aria-label="關閉"
              onClick={(e) => {
                e.stopPropagation();
                setOpenWindow(false);
              }}
              style={{ marginLeft: 4, padding: 2 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </InfoWindow>
      )}
    </Marker>
  );
}
